import React, { useEffect, useState } from 'react';
import Layout from '../components/Layout';
import CustomerUserModal from '../components/CustomerUserModal';
import { customerService, CustomerUser, Company } from '../services/customerService';
import { organizationService, OrgMember } from '../services/organizationService';
import { useActiveProject } from '../hooks/useActiveProject';
import { usePermissions } from '../hooks/usePermissions';
import { useAuth } from '../contexts/AuthContext';
import BoardControlsButton, { BoardColumn, BoardPrefs, loadBoardPrefs, isColumnVisible } from '../components/BoardControlsButton';
import '../styles/CustomersList.css';
import '../styles/Pages.css';

const USER_COLUMNS: BoardColumn[] = [
  { key: 'description', label: 'Description' },
  { key: 'assignee', label: 'Assignee' },
  { key: 'company', label: 'Company' },
  { key: 'email', label: 'Email' },
  { key: 'contact', label: 'Contact' },
  { key: 'insights', label: 'Insights' },
];

const ICON_COLORS = ['#6366f1', '#ec4899', '#f59e0b', '#10b981'];
const ICON_BGS    = ['#ede9fe', '#fce7f3', '#fef3c7', '#d1fae5'];

const sentinel = '__editablejs__:';
function getPlainText(details: string | null | undefined): string {
  if (!details) return '';
  let str = details;
  if (str.startsWith(sentinel)) str = str.slice(sentinel.length);
  if (str.trimStart().startsWith('[') || str.trimStart().startsWith('{')) {
    try {
      const nodes = JSON.parse(str);
      const extractText = (node: any): string => {
        if (typeof node.text === 'string') return node.text;
        if (Array.isArray(node.children)) return node.children.map(extractText).join('');
        return '';
      };
      return (Array.isArray(nodes) ? nodes : [nodes])
        .map(extractText).join(' ').replace(/\s+/g, ' ').trim().slice(0, 100);
    } catch { /* fall through */ }
  }
  return str.replace(/<[^>]+>/g, ' ').replace(/\s+/g, ' ').trim().slice(0, 100);
}

const UserIcon: React.FC<{ color: string }> = ({ color }) => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2">
    <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
    <circle cx="12" cy="7" r="4" />
  </svg>
);

const CustomerUsers: React.FC = () => {
  const { activeProjectId } = useActiveProject();
  const { currentOrg } = useAuth();
  const { can } = usePermissions();
  const canManage = can('customers');

  const [users, setUsers]             = useState<CustomerUser[]>([]);
  const [companies, setCompanies]     = useState<Company[]>([]);
  const [allMembers, setAllMembers]   = useState<OrgMember[]>([]);
  const [loading, setLoading]         = useState(true);
  const [error, setError]             = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [showModal, setShowModal]     = useState(false);
  const [editingUser, setEditingUser] = useState<CustomerUser | null>(null);
  const [boardPrefs, setBoardPrefs] = useState<BoardPrefs>(() => loadBoardPrefs('customerUsers:boardControls'));
  const colVisible = (key: string) => isColumnVisible(boardPrefs.columns, key);

  const load = () => {
    setLoading(true);
    setError(null);
    Promise.all([
      customerService.getCustomerUsers(activeProjectId),
      customerService.getCompanies(activeProjectId),
    ]).then(([us, cos]) => {
      setUsers(us);
      setCompanies(cos);
    }).catch(() => setError('Failed to load users'))
      .finally(() => setLoading(false));
  };

  useEffect(load, [activeProjectId]);

  useEffect(() => {
    if (!currentOrg) return;
    organizationService.getMembersWithProfiles(currentOrg.id)
      .then(setAllMembers).catch(() => {});
  }, [currentOrg]);

  const filtered = users.filter(u =>
    u.name.toLowerCase().includes(searchQuery.trim().toLowerCase())
  );

  const handleSaved = (user: CustomerUser) => {
    setUsers(prev => {
      const exists = prev.some(u => u.id === user.id);
      return exists ? prev.map(u => u.id === user.id ? user : u) : [user, ...prev];
    });
    setShowModal(false);
    setEditingUser(null);
  };

  const handleDelete = async (user: CustomerUser, e: React.MouseEvent) => {
    e.stopPropagation();
    if (!window.confirm(`Delete "${user.name}"? This cannot be undone.`)) return;
    try {
      await customerService.deleteCustomerUser(user.id);
      setUsers(prev => prev.filter(u => u.id !== user.id));
    } catch {
      alert('Failed to delete user.');
    }
  };

  const getMemberName = (userId: string | null) => {
    if (!userId) return null;
    const m = allMembers.find(m => m.user_id === userId);
    return m ? (m.full_name || m.email || null) : null;
  };

  const getCompanyName = (companyId: string | null) => {
    if (!companyId) return null;
    const c = companies.find(c => c.id === companyId);
    return c ? c.name : null;
  };

  return (
    <Layout>
      <div className="customers-list-container">
        <div className="customers-header-row">
          <div className="page-breadcrumb">
            <div className="page-breadcrumb-icon">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
                <circle cx="12" cy="7" r="4" />
              </svg>
            </div>
            <h1 className="page-breadcrumb-title">Users</h1>
          </div>
          <div className="header-right-section">
            <div className="search-bar">
              <svg className="search-icon" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <circle cx="11" cy="11" r="8"/><path d="m21 21-4.35-4.35"/>
              </svg>
              <input type="text" placeholder="Search users..." value={searchQuery}
                onChange={e => setSearchQuery(e.target.value)} className="search-input" />
            </div>
            <BoardControlsButton columns={USER_COLUMNS} storageKey="customerUsers:boardControls" prefs={boardPrefs} onChange={setBoardPrefs} />
            <button className="customers-add-btn btn-primary-cta" onClick={() => { setEditingUser(null); setShowModal(true); }}
              disabled={!canManage} title={canManage ? 'Add a new user' : 'Only owners and admins can add users'}>
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
                <line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/>
              </svg>
              Add User
            </button>
          </div>
        </div>

        {loading && <div className="customers-loading">Loading users…</div>}

        {error && (
          <div className="error-state"><p>{error}</p><button onClick={load}>Retry</button></div>
        )}

        {!loading && !error && filtered.length === 0 && (
          <div className="empty-state">
            <div className="empty-icon">👤</div>
            <h2>{searchQuery ? 'No users found' : 'No customer users yet'}</h2>
            <p>{searchQuery ? 'Try a different search term' : 'Add your first customer user to get started'}</p>
          </div>
        )}

        {!loading && !error && filtered.length > 0 && (
          <div className="clist-table-wrap">
            {/* Table header */}
            <div className="clist-header-row">
              <div className="clist-col clist-col-name">Name</div>
              {colVisible('description') && <div className="clist-col clist-col-desc">Description</div>}
              {colVisible('assignee') && <div className="clist-col clist-col-sm">Assignee</div>}
              {colVisible('company') && <div className="clist-col clist-col-sm">Company</div>}
              {colVisible('email') && <div className="clist-col clist-col-sm">Email</div>}
              {colVisible('contact') && <div className="clist-col clist-col-sm">Contact</div>}
              {colVisible('insights') && <div className="clist-col clist-col-xs">Insights</div>}
              <div className="clist-col clist-col-actions" />
            </div>

            {filtered.map((user, idx) => {
              const colorIdx     = idx % 4;
              const assigneeName = getMemberName(user.assignee_id);
              const companyName  = getCompanyName(user.company_id) ?? user.companies?.name ?? null;
              const preview      = getPlainText(user.details);
              return (
                <div key={user.id} className="clist-row"
                  onClick={() => { if (canManage) { setEditingUser(user); setShowModal(true); } }}
                  style={{ cursor: canManage ? 'pointer' : 'default' }}>

                  {/* Name */}
                  <div className="clist-col clist-col-name">
                    <div className="clist-icon" style={{ background: ICON_BGS[colorIdx] }}>
                      <UserIcon color={ICON_COLORS[colorIdx]} />
                    </div>
                    <span className="clist-name">{user.name}</span>
                  </div>

                  {/* Description */}
                  {colVisible('description') && (
                    <div className="clist-col clist-col-desc">
                      <span className="clist-meta">{preview || <span className="clist-empty">—</span>}</span>
                    </div>
                  )}

                  {/* Assignee */}
                  {colVisible('assignee') && (
                    <div className="clist-col clist-col-sm">
                      {assigneeName
                        ? <span className="clist-pill clist-pill-purple">{assigneeName}</span>
                        : <span className="clist-empty">—</span>}
                    </div>
                  )}

                  {/* Company */}
                  {colVisible('company') && (
                    <div className="clist-col clist-col-sm">
                      {companyName
                        ? <span className="clist-pill clist-pill-indigo">{companyName}</span>
                        : <span className="clist-empty">—</span>}
                    </div>
                  )}

                  {/* Email */}
                  {colVisible('email') && (
                    <div className="clist-col clist-col-sm">
                      <span className="clist-meta">{user.email || <span className="clist-empty">—</span>}</span>
                    </div>
                  )}

                  {/* Contact (phone) */}
                  {colVisible('contact') && (
                    <div className="clist-col clist-col-sm">
                      <span className="clist-meta">{user.phone || <span className="clist-empty">—</span>}</span>
                    </div>
                  )}

                  {/* Insights */}
                  {colVisible('insights') && (
                    <div className="clist-col clist-col-xs">
                      <span className="clist-badge">0</span>
                    </div>
                  )}

                  {/* Actions */}
                  <div className="clist-col clist-col-actions" onClick={e => e.stopPropagation()}>
                    {canManage && (
                      <div className="clist-actions">
                        <button className="clist-action-btn"
                          onClick={e => { e.stopPropagation(); setEditingUser(user); setShowModal(true); }} title="Edit">
                          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
                            <path d="M12 20h9"/><path d="M16.5 3.5a2.121 2.121 0 0 1 3 3L7 19l-4 1 1-4 12.5-12.5z"/>
                          </svg>
                        </button>
                        <button className="clist-action-btn clist-action-danger"
                          onClick={e => handleDelete(user, e)} title="Delete">
                          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
                            <polyline points="3 6 5 6 21 6"/><path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"/>
                          </svg>
                        </button>
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {showModal && (
        <CustomerUserModal customerUser={editingUser}
          onClose={() => { setShowModal(false); setEditingUser(null); }}
          onSaved={handleSaved} />
      )}
    </Layout>
  );
};

export default CustomerUsers;
